<script lang="ts" setup>
import ValidationErrors from "@/components/Common/ValidationErrors.vue";
import { formatDate } from "@/services/functions";
import { ref, Ref, computed, nextTick, watch } from "vue";
import i18n from '@/lang/i18n';

type rangeType = {
  start_at: Date;
  end_at: Date;
};

type selectable = {
  value: string;
  selectable: boolean;
  selected: boolean;
};

const props = defineProps<{
  label: string;
  errors: string[];
  modelValue: string;
  ranges: rangeType[];
  loading: boolean;
  reading: boolean;
}>();

const emit = defineEmits(["update:modelValue"]);
const diffHour = new Date().getTimezoneOffset() / 60;

const globalRange = computed((): { start_at?: Date, end_at?: Date } => {
  if (!props.ranges.length) return { start_at: undefined, end_at: undefined };

  return {
    start_at: setStartOfDay(props.ranges[0].start_at),
    end_at: setEndOfDay(props.ranges[props.ranges.length - 1].end_at)
  };
})

const selectedDate: Ref<selectable["value"] | Date | undefined> = ref();
const selectedHour: Ref<selectable["value"] | Date | undefined> = ref();
const selectedMinute: Ref<selectable["value"] | Date | undefined> = ref();

const dates = computed(() => {
  if (!props.ranges.length) return []
  const start = globalRange.value.start_at;
  const end =  globalRange.value.end_at;

  return createDateList(start, end);
});

const hours = computed(() => {
  return createHourList();
});

const minutes = computed(() => {
  return createMinuteList();
});

watch(() => props.ranges, () => {
  if (!props.ranges[0]) {
    selectedDate.value = undefined;
    selectedHour.value = undefined;
    selectedMinute.value = undefined;
    return;
  }
})

function setStartOfDay(d: Date) {
  let _d = new Date(formatDate(d))
  return new Date(_d.setHours(0))
}

function setEndOfDay(d: Date) {
  let _d = setStartOfDay(d)
  _d = new Date(_d.setDate(_d.getDate() + 1))
  return new Date(_d.setMilliseconds(-1))
}

function localeDiffDate(d: Date) {
  const _d = new Date(d.getTime());
  return new Date(_d.setHours(_d.getHours() - diffHour));
}

const selectedDateTime = computed(() => {
  emit("update:modelValue", "");
  if (!selectedMinute.value) return;
  const _selectedDateTime = `${selectedDate.value} ${selectedHour.value}:${selectedMinute.value}`;
  emit("update:modelValue", _selectedDateTime);
  return _selectedDateTime;
});

function createDateList(start: Date | undefined, end: Date | undefined) {
  const ret: selectable[] = [];

  const startDay = new Date(start.getTime());
  const nextDay = new Date(startDay.getTime());
  nextDay.setDate(startDay.getDate() + 1);

  while (end >= startDay) {
    const formatted = formatDate(localeDiffDate(startDay));
    let selectable = false
    props.ranges.forEach((range: rangeType) => {
      selectable = selectable ||
        new Date(range.start_at).getTime() != new Date(range.end_at).getTime() &&
        range.start_at < nextDay &&
        range.end_at > startDay;
    })
    if (selectable) {
      ret.push({
        value: formatted,
        selectable: selectable,
        selected: selectedDate.value == formatted,
      });
    }
    startDay.setDate(startDay.getDate() + 1);
    nextDay.setDate(nextDay.getDate() + 1);
  }
  return ret.reverse();
}

function createHourList() {
  const ret: selectable[] = [];
  const creatable = !!selectedDate.value;

  let startTime = new Date(selectedDate.value as Date)
  startTime.setHours(startTime.getHours() + diffHour);
  let nextTime = new Date(startTime.getTime());
  nextTime.setHours(nextTime.getHours() + 1);

  for (let h = 0; h < 24; h++) {
    let selectable = false;

    props.ranges.forEach((range: rangeType) => {
      if (
        creatable &&
        new Date(range.start_at).getTime() != new Date(range.end_at).getTime() &&
        range.start_at < nextTime &&
        range.end_at > startTime
      ) {
        selectable = true;
        return;
      }
    });
    const formatted = String(h).padStart(2, '0');
    ret.push({
      value: formatted,
      selectable: selectable,
      selected: selectedHour.value == formatted,
    });
    startTime.setHours(startTime.getHours() + 1);
    nextTime.setHours(nextTime.getHours() + 1);
  }
  return ret;
}

function createMinuteList() {
  const ret: selectable[] = [];
  const creatable = !!selectedHour.value;

  let startTime = new Date(selectedDate.value as Date)
  startTime.setHours(startTime.getHours() + Number(selectedHour.value) + diffHour);
  let nextTime = new Date(startTime.getTime());
  nextTime.setMinutes(nextTime.getMinutes() + 1);

  for (let m = 0; m < 60; m++) {
    let selectable = false;
    props.ranges.forEach((range: rangeType) => {
      if (creatable && range.start_at < nextTime && range.end_at > startTime) {
        selectable = true;
        return;
      }
    });
    const formatted = String(m).padStart(2, '0');
    ret.push({
      value: formatted,
      selectable: selectable,
      selected: selectedMinute.value == formatted,
    });
    startTime.setMinutes(startTime.getMinutes() + 1);
    nextTime.setMinutes(nextTime.getMinutes() + 1);
  }
  return ret;
}

async function selectDate(select: selectable) {
  if (selectedDate.value !== select.value) {
    selectedHour.value = undefined;
    selectedMinute.value = undefined;
  }
  if (!select.selectable) return;

  selectedDate.value = select.value;
  await nextTick();
  scrollHour();
  scrollMinute();
}

async function selectHour(select: selectable) {
  if (selectedHour.value !== select.value) {
    selectedMinute.value = undefined;
  }
  if (!select.selectable) return;

  selectedHour.value = select.value;
  await nextTick();
  scrollMinute();
}

function selectMinute(select: selectable) {
  if (!select.selectable) return;
  selectedMinute.value = select.value;
}

function getListContentClass(data: selectable, index: number) {
  let ret = data.selectable ? "selectable selectable-odd-number" : "unselectable";
  if (data.selectable && index % 2 == 0) ret = "selectable selectable-even-number"
  if (data.selected) return "selected";
  return ret;
}

function scrollHour() {
  scrollTopSelectable("select-time-hour");
}

function scrollMinute() {
  scrollTopSelectable("select-time-minute");
}

function scrollTopSelectable(id: string) {
  const parent = document.getElementById(id);
  const target = parent?.getElementsByClassName("selectable")[0];
  if (!target) return;
  target.scrollIntoView({ behavior: "smooth" });
}
</script>

<template>
  <p><b>{{ label }}: {{ selectedDateTime }}</b></p>
  <div class="d-flex justify-content-between w-100 px-5 h-40">
    <template v-if="loading || props.reading">
      <div class="loading">
        <div class="not-searched">
        </div>
      </div>
    </template>
    <template v-else-if="!ranges[0] || !dates.length">
      <div class="loading">
        <div class="not-searched">
          <label>{{ i18n.global.t('pages.cvr_uploads.requestPanel.deliveryDoesNotExists') }}</label>
        </div>
      </div>
    </template>
    <template v-else>
      <div
        id="select-time-date"
        class="select-time-list select-time-date-list d-flex flex-column"
      >
        <template v-for="(date, index) in dates" v-bind:key="date.value">
          <div
            class="list-element"
            :class="getListContentClass(date, index)"
            @click="selectDate(date)"
          >
            {{ date.value }}
          </div>
        </template>
      </div>
      <div
        id="select-time-hour"
        class="select-time-list select-time-hour-list d-flex flex-column"
      >
        <template v-for="(hour, index) in hours" v-bind:key="hour.value">
          <div
            class="list-element"
            :class="getListContentClass(hour, index)"
            @click="selectHour(hour)"
          >
            {{ hour.value }}{{i18n.global.t('pages.cvr_uploads.hour')}}
          </div>
        </template>
      </div>
      <div
        id="select-time-minute"
        class="select-time-list select-time-minute-list d-flex flex-column"
      >
        <template v-for="(minute, index) in minutes" v-bind:key="minute.value">
          <div
            class="list-element"
            :class="getListContentClass(minute, index)"
            @click="selectMinute(minute)"
          >
            {{ minute.value }}{{i18n.global.t('pages.cvr_uploads.minute')}}
          </div>
        </template>
      </div>
    </template>
  </div>
  <ValidationErrors :messages="errors" />
</template>

<style scoped>
.not-searched {
  height: inherit;
  position: absolute;
  z-index: 1000;
  background-color: rgba(80,80,80,0.5);
  padding: 3%;
}
.not-searched>label {
  font-weight: bold;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}
.select-time-list {
  max-height: 250px;
  height: fit-content;
  overflow: auto;
  border-width: 1px;
}
.select-time-date-list {
  border: solid 1px lightgray;
  width: 35%;
}
.select-time-hour-list,
.select-time-minute-list {
  border: solid 1px lightgray;
  width: 30%;
}
.list-element {
  text-align: center;
  padding: 1vh;
  border-top-width: 1px;
}
.selected {
  background-color: lightseagreen;
  color: white;
}
.selectable-odd-number {
  background-color: whitesmoke;
}
.selectable-even-number {
  background-color: white;
}
.unselectable {
  background-color: silver;
}
.loading {
  position: relative;
  height: 250px;
  width: 100%;
}
</style>
